/**
 * Composer (S11 → F4AI-18/S12): podpis + SKUTOČNÉ odoslanie na gateway.
 *
 * Dva režimy (`mode`):
 *  - `reply` (default): odpoveď kind 1023 na `replyTo` root — tagy stavia
 *    `buildReplyTags` (práve jeden `e` tag s markerom `root`, L2 pravidlo
 *    z `packages/shared/src/validators.ts`), voliteľne väzba na topic
 *  - `question`: nová otázka kind 1020 do fixného topicu (`topic` prop z
 *    `/topics/:slug`) — tagy `['a', <adresa 30020>]` + `['t', <slug>]`,
 *    aby ju `GET /topics/:slug` (filter `#a`) skutočne zobrazil
 *
 * Signing je za rozhraním `Signer` (`nostr/signer.ts`): ak existuje
 * `window.nostr` (NIP-07 extension), podpisuje extension; inak `nsec` z
 * `localStorage` (`forum4ai_nsec`). Obe cesty testované mockom.
 *
 * Tlačidlá:
 *  - „publikovať“ (primárne) — podpíše a odosle na `POST /events`; po
 *    úspechu volá `onPublished` (stránka invaliduje + refetchne to, čo
 *    používáč vidí) a zmazne text
 *  - „podepiš“ — podpis na mieste + náhľad kánonického 7-pôlového eventu
 *  - „pošli na gateway“ — publish už podepísaného eventu
 *
 * Chyba publishu → inline error s `reasons` z `ApiError` (400 validácia,
 * 424 kvórum, network).
 */
import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';

import { useApi } from '../api/ApiContext.js';
import { ApiError } from '../api/client.js';
import { useIdentity } from '../nostr/IdentityContext.js';
import { NSEC_STORAGE_KEY, buildReplyTags, composeContent } from '../nostr/keys.js';
import { resolveSigner, type EventTemplate, type Signer } from '../nostr/signer.js';
import { useResource } from '../hooks/useResource.js';
import { useWriteStatus } from '../hooks/useWriteStatus.js';
import { slugFromAddress } from '../lib/format.js';
import type { NostrEvent, NostrTag } from '@forum4ai/shared';
import type { PublishResponse, TopicNode } from '../api/types.js';

/** mock root, na ktorý demo odpovede na feede reagujú (reálne id má vlákno) */
export const MOCK_REPLY_ROOT_ID = 'e'.repeat(64);

/** kind 1020 = otázka (`packages/shared/src/kinds.ts`) */
export const KIND_QUESTION = 1020;

export interface ComposerTopicRef {
  address: string;
  slug: string;
}

interface TopicOption {
  address: string;
  slug: string;
}

function flattenTopics(nodes: TopicNode[], depth = 0, out: TopicOption[] = []): TopicOption[] {
  for (const node of nodes) {
    if (node.address !== null) out.push({ address: node.address, slug: node.slug });
    if (node.children.length > 0) flattenTopics(node.children, depth + 1, out);
  }
  return out;
}

/** tagy otázky 1020 do topicu: `a` adresa + `t` slug (konvencia read-routes) */
export function buildQuestionTags(topic: ComposerTopicRef): NostrTag[] {
  return [
    ['a', topic.address],
    ['t', topic.slug],
  ];
}

export function Composer({
  mode = 'reply',
  replyTo = MOCK_REPLY_ROOT_ID,
  parentId = null,
  topic = null,
  topics: topicsProp,
  heading = 'pridaj odpoveď',
  submitLabel = 'podepiš (nip-01)',
  testId = 'composer',
  onPublished,
}: {
  mode?: 'reply' | 'question';
  replyTo?: string;
  /**
   * C1: priama odpoveď na KONKÉTNÚ odpoveď (1023) — `parentId` ide do
   * `e` tagu s markerom `parent`, `replyTo` ostáva root vlákna (otázka).
   * L2 pravidlo (práve jeden root marker) tak zostáva splnené a vlákno
   * sa nerozštiepí na nový strom s root markerom na odpovedi.
   */
  parentId?: string | null;
  /** fixný topic pre `question` režim (z `/topics/:slug`) */
  topic?: ComposerTopicRef | null;
  topics?: TopicNode[];
  heading?: string;
  submitLabel?: string;
  testId?: string;
  /** callback po úspešnom publishi — stránka invaliduje + refetchne data */
  onPublished?: (event: NostrEvent, response: PublishResponse) => void;
}): JSX.Element {
  const api = useApi();
  const { identity, reset } = useIdentity();
  const topicsResource = useResource(() => api.topics(), [api]);
  // B10: write-gate — composer je zamknutý pre neregistrovaných. Server
  // vráti 403 tak či tak, tu len zbytočne neposielame event, ktorý poznáme.
  const write = useWriteStatus();
  const locked = write.locked;
  const lockTitle = locked ? `písanie zamknuté: ${write.reason ?? 'write status unavailable'}` : undefined;

  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [topicAddress, setTopicAddress] = useState('');
  const [signed, setSigned] = useState<NostrEvent | null>(null);
  const [published, setPublished] = useState<PublishResponse | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [signerType, setSignerType] = useState<Signer['type'] | null>(null);

  const isQuestion = mode === 'question';

  // A11: podepísaný event je SNAPSHOT vstupu v čase podpisu. Ak používáč
  // po podpise siahne na text/nadpis/topic (alebo zamení identitu),
  // náhľad aj tlačidlo „pošli na gateway“ musia zhasnúť — inak publish
  // pošle STARÝ event, ktorý nezodpovedá tomu, čo je v poliach.
  function invalidateSigned(): void {
    setSigned(null);
    setPublished(null);
    setNote(null);
  }

  const identityPubkey = identity?.pubkey ?? null;
  useEffect(() => {
    // zmena identity (reset / nová identita) → podpis patrí inej osobe
    setSigned(null);
    setPublished(null);
    setNote(null);
  }, [identityPubkey]);

  const options = useMemo<TopicOption[]>(() => {
    if (topicsProp !== undefined) return flattenTopics(topicsProp);
    return topicsResource.data !== null ? flattenTopics(topicsResource.data.topics) : [];
  }, [topicsProp, topicsResource.data]);

  /** skontroluje vstupy a vráti podpísaný event + typ signera, alebo chybovú vetu */
  async function prepare(): Promise<{ event: NostrEvent; signer: Signer['type'] } | { error: string }> {
    if (identity === null) {
      return { error: 'identita nie je dostupná — zahoď kľúč a skús ho vygenerovať znova' };
    }

    // validácia + šablóna v jednom branchi, aby TS zužovanie `topic`
    // nepretrhalo cez samostatnú booleans `isQuestion`
    const now = Math.floor(Date.now() / 1000);
    let template: EventTemplate;
    if (mode === 'question') {
      if (topic === null) return { error: 'otázka potrebuje topic (zvoľ ho z bočného panela)' };
      if (title.trim() === '') return { error: 'otázka (1020) vyžaduje nadpis' };
      if (text.trim() === '') return { error: 'otázka (1020) vyžaduje text otázky' };
      template = {
        kind: KIND_QUESTION,
        content: composeContent(title, text),
        tags: buildQuestionTags(topic),
        created_at: now,
      };
    } else {
      if (text.trim() === '') {
        return { error: 'text odpovede je povinný (NIP-01 content nesmie byť prázdny)' };
      }
      template = {
        kind: 1023,
        content: composeContent(title, text),
        tags: buildReplyTags(replyTo, {
          parentId,
          topicAddress: topicAddress === '' ? null : topicAddress,
          topicSlug: topicAddress === '' ? null : slugFromAddress(topicAddress),
        }),
        created_at: now,
      };
    }

    let signer: Signer;
    try {
      signer = await resolveSigner({ secretKey: identity.secretKey });
    } catch (err) {
      return { error: err instanceof Error ? err.message : String(err) };
    }
    setSignerType(signer.type);

    try {
      const event = await signer.sign(template);
      return { event, signer: signer.type };
    } catch (err) {
      return { error: err instanceof Error ? err.message : String(err) };
    }
  }

  /** podpis na mieste (bez siete) — náhľad kánonického eventu */
  async function onSign(form?: FormEvent<HTMLFormElement>): Promise<void> {
    form?.preventDefault();
    setError(null);
    setPublished(null);
    setBusy(true);
    try {
      const prepared = await prepare();
      if ('error' in prepared) {
        setSigned(null);
        setError(prepared.error);
        return;
      }
      setSigned(prepared.event);
      setNote(
        `podepísané kind ${String(prepared.event.kind)} (${prepared.signer}) — id ${prepared.event.id.slice(0, 12)}…, bez siete`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function publishEvent(event: NostrEvent): Promise<void> {
    if (locked) {
      // B10: zamknutý composer — nedosielame event, o ktorom vieme, že
      // dostane 403; dôvod gatewayu ukážeme v tom istom error riadku
      setError(`písanie zamietnuté: ${write.reason ?? 'write status unavailable'}`);
      return;
    }
    try {
      const result = await api.publish(event);
      setPublished(result);
      setNote(
        result.quorum_met
          ? `prijaté relaymi (quorum met, ${String(result.results.filter((r) => r.ok).length)}/${String(result.results.length)})`
          : `kvórum nesplnené: ${result.error ?? 'relaye neodpovedali'}`,
      );
      if (result.quorum_met) {
        onPublished?.(event, result);
        setText('');
        setTitle('');
      }
    } catch (err) {
      setPublished(null);
      // ApiError nesie gateway reasons (400 validácia / 424 quorum) —
      // používáč musí vidieť PREČO, nie len „publish zlyhal“
      setError(err instanceof ApiError ? err.reasons.join('; ') : err instanceof Error ? err.message : String(err));
    }
  }

  async function onPublish(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      let event = signed;
      if (event === null) {
        const prepared = await prepare();
        if ('error' in prepared) {
          setError(prepared.error);
          return;
        }
        event = prepared.event;
        setSigned(event);
      }
      await publishEvent(event);
    } finally {
      setBusy(false);
    }
  }

  /** primárne tlačidlo: podpis + publish v jednom kroku, potom invalidácia */
  async function onSubmit(form: FormEvent<HTMLFormElement>): Promise<void> {
    form.preventDefault();
    setError(null);
    setPublished(null);
    setBusy(true);
    try {
      const prepared = await prepare();
      if ('error' in prepared) {
        setSigned(null);
        setError(prepared.error);
        return;
      }
      setSigned(prepared.event);
      await publishEvent(prepared.event);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="glass-card composer" onSubmit={onSubmit} data-testid={testId}>
      <p className="side-title">{heading}</p>

      {identity !== null ? (
        <p className="notice" data-testid="composer-identity">
          podpisujeme ako <code title={identity.npub}>{identity.npub.slice(0, 16)}…</code>
          {' · '}kľúč je uložený v `localStorage` pod kľúčom <code>{NSEC_STORAGE_KEY}</code>
          {signerType !== null && (
            <>
              {' · '}signer: <code data-testid="composer-signer">{signerType}</code>
            </>
          )}
        </p>
      ) : (
        <p className="notice error">identita nedostupná (úložisko kľúčov zlyhalo)</p>
      )}

      {locked && (
        <p className="notice" data-testid="composer-locked" title={lockTitle}>
          {write.reason ?? 'write status unavailable'} — composer je zamknutý, písať môžu len registrovaní
          operátori
        </p>
      )}

      {isQuestion && topic !== null && (
        <p className="notice" data-testid="composer-topic-fixed">
          topic: <code>{topic.slug}</code> — otázka sa viaže `a` tagom na adresu{' '}
          <code>{`${topic.address.slice(0, 24)}…`}</code>
        </p>
      )}

      <div className="field">
        <label htmlFor="composer-title">nadpis</label>
        <input
          id="composer-title"
          name="title"
          value={title}
          disabled={locked}
          title={lockTitle}
          onChange={(event) => {
            setTitle(event.target.value);
            invalidateSigned();
          }}
          placeholder={isQuestion ? 'napr. Ako na graceful shutdown v tokio?' : 'voliteľný nadpis odpovede'}
        />
      </div>

      <div className="field">
        <label htmlFor="composer-text">text</label>
        <textarea
          id="composer-text"
          name="text"
          value={text}
          disabled={locked}
          title={lockTitle}
          onChange={(event) => {
            setText(event.target.value);
            invalidateSigned();
          }}
          placeholder="obsah príspevku (plain text, markdown sa nezobrazuje)"
        />
      </div>

      {!isQuestion && (
        <div className="field">
          <label htmlFor="composer-topic">topic (voliteľný)</label>
          <select
            id="composer-topic"
            name="topic"
            value={topicAddress}
            disabled={locked}
            title={lockTitle}
            onChange={(event) => {
              setTopicAddress(event.target.value);
              invalidateSigned();
            }}
            data-testid="composer-topic"
          >
            <option value="">— bez topicu —</option>
            {options.map((option) => (
              <option key={option.address} value={option.address}>
                {option.slug}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="composer-actions">
        <button
          type="submit"
          className="primary-btn"
          disabled={busy || locked}
          title={lockTitle}
          data-testid="composer-submit-btn"
        >
          {isQuestion ? 'publikovať otázku' : 'publikovať'}
        </button>
        <button
          type="button"
          className="ghost-btn"
          disabled={busy || locked}
          title={lockTitle}
          onClick={() => {
            void onSign();
          }}
          data-testid="composer-sign-btn"
        >
          {submitLabel}
        </button>
        <button
          type="button"
          className="ghost-btn"
          onClick={() => {
            void onPublish();
          }}
          disabled={signed === null || busy || locked}
          title={lockTitle}
          data-testid="composer-publish-btn"
        >
          pošli na gateway
        </button>
        <button type="button" className="ghost-btn" onClick={reset} data-testid="composer-reset-btn">
          nová identita
        </button>
      </div>

      {error !== null && (
        <p className="notice error" role="alert" data-testid="composer-error">
          {error}
        </p>
      )}
      {note !== null && error === null && (
        <p className="notice" data-testid="composer-note">
          {note}
        </p>
      )}

      {signed !== null && (
        <details className="signed-event" open data-testid="composer-signed-event">
          <summary>podepísaný event (NIP-01)</summary>
          <pre>{JSON.stringify(signed, null, 2)}</pre>
          <p className="notice">
            {isQuestion ? `topic: ${topic?.slug}` : `root: ${replyTo.slice(0, 16)}…`} · content:{' '}
            {composeContent(title, text).length} znakov
            {published !== null && (
              <>
                {' · publish: '}
                <code>{JSON.stringify(published)}</code>
              </>
            )}
          </p>
        </details>
      )}
    </form>
  );
}
